<template>
  <panel-layout
    id="productCategory-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div class="flex items-center">
        <o-tooltip v-if="productCategory && !productCategory.isNewRecord" :text="$t('general.delete')">
          <o-button icon="delete" variant="danger" flat @click="handleDelete" />
        </o-tooltip>
      </div>
    </template>

    <template #heading>
      <div v-if="productCategory || isUncategorised" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ isUncategorised ? $t('general.noCategory') : productCategory.isNewRecord ? $t('product.newCategory') : productCategory.name }}
          </h2>
          <div v-if="productCategory && !productCategory.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.productCategory.edit.id', { id: productCategory.idProductCategory })" />
          </div>
        </div>
      </div>
    </template>

    <o-form ref="form" class="h-full w-full">
      <o-scroll class="h-full w-full">
        <div class="w-full px-6 py-4">
        <template v-if="productCategory">
          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" :validation-value="productCategory.name" class="w-full" :label="$t('general.title')">
              <text-field v-model="productCategory.name" :label="$t('general.title')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group class="w-full" :label="$t('general.information')" :info="$t('pages.productCategory.edit.informationInfo')">
              <text-area-field v-model="productCategory.information" :label="$t('general.information')" />
            </o-field-group>
          </div>
        </template>

        <div v-if="products" class="flex space-x-4 mb-6">
          <o-field-group class="w-full" :label="$t('general.products')" :info="$t('pages.productCategory.edit.productsInfo')">
            <div v-if="!products.length" class="text-sm text-center p-4 bg-gray-50 rounded-lg">
              <p class="text-gray-800 font-semibold dark:text-gray-100">
                {{ $t('pages.productCategory.edit.noProducts') }}
              </p>
              <p class="text-gray-600 dark:text-gray-300">
                {{ $t('pages.productCategory.edit.addProducts') }}
              </p>
            </div>

            <draggable
              v-model="products"
              handle=".reorder-handle"
              item-key="idProduct"
              class="pt-3"
            >
              <template #item="{ element: product }">
                <div class="bg-gray-50 rounded-lg mb-1 p-2">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-1">
                      <o-button icon="reorderHandle" size="sm" flat class="reorder-handle" />

                      <div class="text-sm leading-none pl-4 flex items-center">
                        <p class="font-semibold text-gray-800 pr-3 border-r mr-3 dark:text-gray-100">
                          {{ product.name }}
                          <span class="text-xs font-normal text-gray-600 dark:text-gray-300">(#{{ product.idProduct }})</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </o-field-group>
        </div>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="productCategory || products">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <delete-dialog
      ref="deleteDialog"
      @success="cancel(true)"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import draggable from 'vuedraggable'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
import DeleteDialog from '@/components/productCategory/actions/Delete.vue'

import {
  TextField,
  TextAreaField,
} from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const productCategory = ref<any>(null)
const products = ref<any>(null)
const errors = ref<any>(false)
const loading = ref(true)

const confirmDialog = ref<any>()
const deleteDialog = ref<any>()
const form = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(productCategory)
const isUncategorised = computed(() => props.id === '0')

const fetchProductCategory = async (id?: string) => {
  productCategory.value = null

  try {
    if (!id) {
      productCategory.value = (await $api.productCategory.defaults({} as any)).data
    } else if (id !== '0') {
      productCategory.value = (await $api.productCategory.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
}

const fetchProducts = async (id?: string) => {
  products.value = null

  try {
    if (id) {
      const ids = id === '0'
        ? await $api.product.uncategorisedIDs()
        : (await $api.product.filter({ category: { categories: [id] } })).data.ids

      const listResponse = await $api.product.listIDs(ids.map(String))
      const _products = listResponse.items
      products.value = _products?.sort((a: any, b: any) => {
        return a?.listOrder - b?.listOrder
      })
    }
  } catch (e) {
    console.log(e)
  }
}

const handleDelete = () => {
  deleteDialog.value.open(productCategory.value)
}

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const _productCategory = productCategory.value ? (await $api.productCategory.save(productCategory.value)).data : true

    if (_productCategory) {
      snapshot()

      const reorder = products.value?.length ? await $api.product.reorder(products.value) : true

      if (reorder) {
        $notification.toast({
          title: $i18n.t('pages.productCategory.edit.success'),
          text: $i18n.t('pages.productCategory.edit.successInfo'),
          variant: 'success',
        })

        $bus.emit.updated('ProductCategory', _productCategory)

        if (close) {
          panel.close()
        }
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

watch(() => props.id, async (id) => {
  loading.value = true

  await Promise.all([
    fetchProductCategory(id),
    fetchProducts(id),
  ])

  loading.value = false
}, { immediate: true })
</script>
